import { motion } from 'framer-motion';

import { type Masked, unmask } from '~/contentful/graphql';
import { flattenSingletonCollection } from '~/contentful/utils/collection';
import { filterNonOptional } from '~/contentful/utils/optional';
import { ClientSearch } from '~/ui/components/search/search.client';
import searchStyles from '~/ui/components/search/search.module.scss';
import { Suggestions } from '~/ui/components/search/suggestions/suggestions';
import { Route } from '~/ui/systems/internalLink/route';
import { encodeSearchQuery } from '~/ui/systems/search';

import styles from '../secondary.module.scss';
import { type SecondaryNavigationSearch_QueryFragment } from './query';

interface SecondaryNavigationSearchProps extends React.HTMLAttributes<HTMLInputElement> {
  data: Masked<typeof SecondaryNavigationSearch_QueryFragment>;
}

export const SecondaryNavigationSearch: React.FC<SecondaryNavigationSearchProps> = ({
  data,
  ...props
}) => {
  const { searchPageCollection } = unmask<typeof SecondaryNavigationSearch_QueryFragment>(data);
  const searchPage = flattenSingletonCollection(searchPageCollection);
  const featuredSearches = filterNonOptional(searchPage.featuredSearches).map(featuredSearch => ({
    title: featuredSearch,
    href: encodeSearchQuery(Route.SEARCH, featuredSearch),
  }));

  return (
    <motion.div
      className={styles.searchWrapper}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0,
        },
      }}
      transition={{ delay: 0.2, duration: 0.5, ease: [0.12, 0, 0.39, 0] }}
    >
      <ClientSearch endpoint={Route.SEARCH} fullWidth {...props}>
        {featuredSearches.length && (
          <div className={searchStyles.suggestions}>
            <Suggestions label="Featured searches" suggestions={featuredSearches} fullWidth />
          </div>
        )}
      </ClientSearch>
    </motion.div>
  );
};
